import React, { useEffect, useState, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import CodeBlock from '@tiptap/extension-code-block';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Underline from '@tiptap/extension-underline';
import YouTube from '@tiptap/extension-youtube';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import CharacterCount from '@tiptap/extension-character-count';
import DOMPurify from 'dompurify';
import {
   Bold,
   Italic,
   Underline as UnderlineIcon,
   Strikethrough,
   Superscript as SuperscriptIcon,
   Subscript as SubscriptIcon,
   AlignLeft,
   AlignCenter,
   AlignRight,
   AlignJustify,
   List,
   ListOrdered,
   ListChecks,
   Quote,
   Code,
   Minus,
   Link as LinkIcon,
   Image as ImageIcon,
   Youtube,
   Table as TableIcon,
   Undo2,
   Redo2,
   ChevronDown,
   Palette,
   Highlighter,
   X,
   Upload,
   ExternalLink,
} from 'lucide-react';

const DOMPURIFY_CONFIG = {
   ADD_TAGS: ['mark', 'sup', 'sub', 'input', 'label'],
   ADD_ATTR: ['style', 'data-checked', 'data-type', 'type', 'checked', 'disabled', 'data-color'],
   ALLOW_DATA_ATTR: true,
};

const PRESET_COLORS = [
   '#000000',
   '#434343',
   '#666666',
   '#999999',
   '#b7b7b7',
   '#cccccc',
   '#d9d9d9',
   '#ffffff',
   '#980000',
   '#ff0000',
   '#ff9900',
   '#ffff00',
   '#00ff00',
   '#00ffff',
   '#4a86e8',
   '#0000ff',
   '#9900ff',
   '#ff00ff',
   '#e6b8af',
   '#f4cccc',
   '#fce5cd',
   '#fff2cc',
   '#d9ead3',
   '#d0e0e3',
   '#c9daf8',
   '#cfe2f3',
   '#d9d2e9',
   '#ead1dc',
   '#dd7e6b',
   '#ea9999',
   '#f9cb9c',
   '#ffe599',
   '#b6d7a8',
   '#a2c4c9',
   '#a4c2f4',
   '#9fc5e8',
   '#b4a7d6',
   '#d5a6bd',
];

// --- Sub-components ---

const ToolbarButton = ({ onClick, isActive, disabled, title, children }) => (
   <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      type="button"
      className={`p-1.5 rounded transition-colors ${
         isActive
            ? 'bg-blue-500 text-white'
            : disabled
              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
      }`}
   >
      {children}
   </button>
);

const ToolbarDivider = () => (
   <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-0.5 self-center" />
);

const BlockTypeDropdown = ({ editor }) => {
   const [open, setOpen] = useState(false);
   const ref = useRef(null);

   useEffect(() => {
      const handleClickOutside = (e) => {
         if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
   }, []);

   const options = [
      { label: 'Paragraph', action: () => editor.chain().focus().setParagraph().run() },
      {
         label: 'Heading 1',
         action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      },
      {
         label: 'Heading 2',
         action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      },
      {
         label: 'Heading 3',
         action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      },
      {
         label: 'Heading 4',
         action: () => editor.chain().focus().toggleHeading({ level: 4 }).run(),
      },
   ];

   const getCurrentLabel = () => {
      for (let i = 1; i <= 4; i++) {
         if (editor.isActive('heading', { level: i })) return `Heading ${i}`;
      }
      return 'Paragraph';
   };

   return (
      <div className="relative" ref={ref}>
         <button
            onClick={() => setOpen(!open)}
            type="button"
            className="flex items-center gap-1 px-2 py-1 rounded text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 min-w-[110px]"
         >
            <span>{getCurrentLabel()}</span>
            <ChevronDown size={14} />
         </button>
         {open && (
            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 py-1 min-w-[140px]">
               {options.map((opt) => (
                  <button
                     key={opt.label}
                     onClick={() => {
                        opt.action();
                        setOpen(false);
                     }}
                     type="button"
                     className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                     {opt.label}
                  </button>
               ))}
            </div>
         )}
      </div>
   );
};

const ColorPickerPopover = ({ editor, type }) => {
   const [open, setOpen] = useState(false);
   const ref = useRef(null);

   useEffect(() => {
      const handleClickOutside = (e) => {
         if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
   }, []);

   const isHighlight = type === 'highlight';
   const IconComponent = isHighlight ? Highlighter : Palette;
   const title = isHighlight ? 'Highlight Color' : 'Text Color';

   const applyColor = (color) => {
      if (isHighlight) {
         editor.chain().focus().toggleHighlight({ color }).run();
      } else {
         editor.chain().focus().setColor(color).run();
      }
      setOpen(false);
   };

   const removeColor = () => {
      if (isHighlight) {
         editor.chain().focus().unsetHighlight().run();
      } else {
         editor.chain().focus().unsetColor().run();
      }
      setOpen(false);
   };

   return (
      <div className="relative" ref={ref}>
         <ToolbarButton
            onClick={() => setOpen(!open)}
            isActive={
               isHighlight
                  ? editor.isActive('highlight')
                  : !!editor.getAttributes('textStyle').color
            }
            title={title}
         >
            <IconComponent size={16} />
         </ToolbarButton>
         {open && (
            <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 p-2 w-[220px]">
               <div className="grid grid-cols-8 gap-1 mb-2">
                  {PRESET_COLORS.map((color) => (
                     <button
                        key={color}
                        onClick={() => applyColor(color)}
                        type="button"
                        className="w-6 h-6 rounded border border-gray-300 dark:border-gray-500 hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                        title={color}
                     />
                  ))}
               </div>
               <button
                  onClick={removeColor}
                  type="button"
                  className="w-full text-xs text-center py-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
               >
                  Remove {isHighlight ? 'highlight' : 'color'}
               </button>
            </div>
         )}
      </div>
   );
};

const LinkPopover = ({ editor, onClose }) => {
   const [url, setUrl] = useState(editor.getAttributes('link').href || '');
   const inputRef = useRef(null);

   useEffect(() => {
      inputRef.current?.focus();
   }, []);

   const apply = () => {
      if (!url) {
         editor.chain().focus().extendMarkRange('link').unsetLink().run();
      } else {
         editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
      }
      onClose();
   };

   const remove = () => {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      onClose();
   };

   return (
      <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 p-3 w-[320px]">
         <div className="flex items-center gap-2 mb-2">
            <ExternalLink size={14} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Link URL</span>
         </div>
         <div className="flex gap-2">
            <input
               ref={inputRef}
               type="url"
               value={url}
               onChange={(e) => setUrl(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && apply()}
               placeholder="https://example.com"
               className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
               onClick={apply}
               type="button"
               className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
            >
               Apply
            </button>
            {editor.isActive('link') && (
               <button
                  onClick={remove}
                  type="button"
                  className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  title="Remove link"
               >
                  <X size={16} />
               </button>
            )}
         </div>
      </div>
   );
};

const ImagePopover = ({ editor, onImageUpload, onClose }) => {
   const [url, setUrl] = useState('');
   const inputRef = useRef(null);

   useEffect(() => {
      inputRef.current?.focus();
   }, []);

   const applyUrl = () => {
      if (url) {
         editor.chain().focus().setImage({ src: url }).run();
      }
      onClose();
   };

   const handleUpload = () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async (e) => {
         const file = e.target.files[0];
         if (file && onImageUpload) {
            try {
               const uploadedUrl = await onImageUpload(file);
               editor.chain().focus().setImage({ src: uploadedUrl }).run();
            } catch (error) {
               console.error('Error uploading image:', error);
            }
         }
         onClose();
      };
      input.click();
   };

   return (
      <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 p-3 w-[320px]">
         <div className="flex items-center gap-2 mb-2">
            <ImageIcon size={14} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
               Insert Image
            </span>
         </div>
         <div className="flex gap-2 mb-2">
            <input
               ref={inputRef}
               type="url"
               value={url}
               onChange={(e) => setUrl(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && applyUrl()}
               placeholder="https://example.com/image.jpg"
               className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
               onClick={applyUrl}
               type="button"
               className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
            >
               Apply
            </button>
         </div>
         {onImageUpload && (
            <button
               onClick={handleUpload}
               type="button"
               className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
               <Upload size={14} />
               Upload from device
            </button>
         )}
      </div>
   );
};

const YouTubePopover = ({ editor, onClose }) => {
   const [url, setUrl] = useState('');
   const [error, setError] = useState('');
   const inputRef = useRef(null);

   useEffect(() => {
      inputRef.current?.focus();
   }, []);

   const apply = () => {
      if (!url) {
         onClose();
         return;
      }
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      const videoId = match && match[2].length === 11 ? match[2] : null;
      if (videoId) {
         editor
            .chain()
            .focus()
            .setYoutubeVideo({ src: `https://www.youtube.com/embed/${videoId}` })
            .run();
         onClose();
      } else {
         setError('Invalid YouTube URL. Please use a valid YouTube link.');
      }
   };

   return (
      <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 p-3 w-[320px]">
         <div className="flex items-center gap-2 mb-2">
            <Youtube size={14} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
               YouTube Video
            </span>
         </div>
         <div className="flex gap-2">
            <input
               ref={inputRef}
               type="url"
               value={url}
               onChange={(e) => {
                  setUrl(e.target.value);
                  setError('');
               }}
               onKeyDown={(e) => e.key === 'Enter' && apply()}
               placeholder="https://www.youtube.com/watch?v=..."
               className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
               onClick={apply}
               type="button"
               className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
            >
               Apply
            </button>
         </div>
         {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>
   );
};

// --- Main Toolbar ---

const MenuBar = ({ editor, onImageUpload }) => {
   const [activePopover, setActivePopover] = useState(null);
   const popoverRefs = {
      link: useRef(null),
      image: useRef(null),
      youtube: useRef(null),
   };

   useEffect(() => {
      const handleClickOutside = (e) => {
         if (activePopover) {
            const ref = popoverRefs[activePopover];
            if (ref.current && !ref.current.contains(e.target)) {
               setActivePopover(null);
            }
         }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [activePopover]);

   if (!editor) return null;

   const togglePopover = (name) => {
      setActivePopover(activePopover === name ? null : name);
   };

   const iconSize = 16;

   return (
      <div className="border-b border-gray-200 dark:border-gray-700 p-1.5 flex flex-wrap items-center gap-0.5">
         {/* Undo / Redo */}
         <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
         >
            <Undo2 size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
         >
            <Redo2 size={iconSize} />
         </ToolbarButton>

         <ToolbarDivider />

         {/* Block Type */}
         <BlockTypeDropdown editor={editor} />

         <ToolbarDivider />

         {/* Inline Formatting */}
         <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            title="Bold"
         >
            <Bold size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            title="Italic"
         >
            <Italic size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            title="Underline"
         >
            <UnderlineIcon size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
            title="Strikethrough"
         >
            <Strikethrough size={iconSize} />
         </ToolbarButton>

         <ToolbarDivider />

         {/* Superscript / Subscript */}
         <ToolbarButton
            onClick={() => editor.chain().focus().toggleSuperscript().run()}
            isActive={editor.isActive('superscript')}
            title="Superscript"
         >
            <SuperscriptIcon size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().toggleSubscript().run()}
            isActive={editor.isActive('subscript')}
            title="Subscript"
         >
            <SubscriptIcon size={iconSize} />
         </ToolbarButton>

         <ToolbarDivider />

         {/* Color Pickers */}
         <ColorPickerPopover editor={editor} type="color" />
         <ColorPickerPopover editor={editor} type="highlight" />

         <ToolbarDivider />

         {/* Text Alignment */}
         <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            isActive={editor.isActive({ textAlign: 'left' })}
            title="Align Left"
         >
            <AlignLeft size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            isActive={editor.isActive({ textAlign: 'center' })}
            title="Align Center"
         >
            <AlignCenter size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            isActive={editor.isActive({ textAlign: 'right' })}
            title="Align Right"
         >
            <AlignRight size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            isActive={editor.isActive({ textAlign: 'justify' })}
            title="Justify"
         >
            <AlignJustify size={iconSize} />
         </ToolbarButton>

         <ToolbarDivider />

         {/* Lists */}
         <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title="Bullet List"
         >
            <List size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title="Ordered List"
         >
            <ListOrdered size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().toggleTaskList().run()}
            isActive={editor.isActive('taskList')}
            title="Task List"
         >
            <ListChecks size={iconSize} />
         </ToolbarButton>

         <ToolbarDivider />

         {/* Block Elements */}
         <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            title="Blockquote"
         >
            <Quote size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive('codeBlock')}
            title="Code Block"
         >
            <Code size={iconSize} />
         </ToolbarButton>
         <ToolbarButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title="Horizontal Rule"
         >
            <Minus size={iconSize} />
         </ToolbarButton>

         <ToolbarDivider />

         {/* Link */}
         <div className="relative" ref={popoverRefs.link}>
            <ToolbarButton
               onClick={() => togglePopover('link')}
               isActive={editor.isActive('link')}
               title="Link"
            >
               <LinkIcon size={iconSize} />
            </ToolbarButton>
            {activePopover === 'link' && (
               <LinkPopover editor={editor} onClose={() => setActivePopover(null)} />
            )}
         </div>

         {/* Image */}
         <div className="relative" ref={popoverRefs.image}>
            <ToolbarButton onClick={() => togglePopover('image')} title="Image">
               <ImageIcon size={iconSize} />
            </ToolbarButton>
            {activePopover === 'image' && (
               <ImagePopover
                  editor={editor}
                  onImageUpload={onImageUpload}
                  onClose={() => setActivePopover(null)}
               />
            )}
         </div>

         {/* YouTube */}
         <div className="relative" ref={popoverRefs.youtube}>
            <ToolbarButton onClick={() => togglePopover('youtube')} title="YouTube">
               <Youtube size={iconSize} />
            </ToolbarButton>
            {activePopover === 'youtube' && (
               <YouTubePopover editor={editor} onClose={() => setActivePopover(null)} />
            )}
         </div>

         {/* Table */}
         <ToolbarButton
            onClick={() =>
               editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
            title="Insert Table"
         >
            <TableIcon size={iconSize} />
         </ToolbarButton>
      </div>
   );
};

// --- Main Editor Component ---

const TipTapEditor = ({ content, onChange, onImageUpload }) => {
   const isInternalUpdate = useRef(false);

   const editor = useEditor({
      extensions: [
         StarterKit,
         Underline,
         Image.configure({
            inline: true,
            allowBase64: true,
         }),
         Link.configure({
            openOnClick: false,
            HTMLAttributes: {
               target: '_blank',
               rel: 'noopener noreferrer',
            },
         }),
         CodeBlock,
         Table.configure({
            resizable: true,
         }),
         TableRow,
         TableHeader,
         TableCell,
         YouTube.configure({
            controls: true,
            nocookie: true,
         }),
         TextAlign.configure({
            types: ['heading', 'paragraph'],
         }),
         TextStyle,
         Color,
         Highlight.configure({
            multicolor: true,
         }),
         Placeholder.configure({
            placeholder: 'Start writing your blog content...',
         }),
         TaskList,
         TaskItem.configure({
            nested: true,
         }),
         Superscript,
         Subscript,
         CharacterCount,
      ],
      content: DOMPurify.sanitize(content || '', DOMPURIFY_CONFIG),
      onUpdate: ({ editor }) => {
         const html = editor.getHTML();
         isInternalUpdate.current = true;
         onChange(DOMPurify.sanitize(html, DOMPURIFY_CONFIG));
      },
   });

   useEffect(() => {
      if (editor && content !== undefined) {
         if (isInternalUpdate.current) {
            isInternalUpdate.current = false;
            return;
         }
         const currentContent = editor.getHTML();
         if (currentContent !== content) {
            editor.commands.setContent(DOMPurify.sanitize(content || '', DOMPURIFY_CONFIG));
         }
      }
   }, [content, editor]);

   const [bubbleMenuPos, setBubbleMenuPos] = useState(null);
   const editorWrapperRef = useRef(null);

   useEffect(() => {
      if (!editor) return;

      const updateBubbleMenu = () => {
         const { from, to, empty } = editor.state.selection;
         if (empty || from === to) {
            setBubbleMenuPos(null);
            return;
         }
         const view = editor.view;
         const start = view.coordsAtPos(from);
         const end = view.coordsAtPos(to);
         const wrapperRect = editorWrapperRef.current?.getBoundingClientRect();
         if (!wrapperRect) return;

         setBubbleMenuPos({
            top: start.top - wrapperRect.top - 44,
            left: (start.left + end.left) / 2 - wrapperRect.left,
         });
      };

      editor.on('selectionUpdate', updateBubbleMenu);
      editor.on('blur', () => setBubbleMenuPos(null));
      return () => {
         editor.off('selectionUpdate', updateBubbleMenu);
         editor.off('blur', () => setBubbleMenuPos(null));
      };
   }, [editor]);

   const iconSize = 16;

   return (
      <div
         ref={editorWrapperRef}
         className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-dark-secondary relative flex flex-col h-[600px]"
      >
         <MenuBar editor={editor} onImageUpload={onImageUpload} />

         {/* Bubble Menu */}
         {editor && bubbleMenuPos && (
            <div
               className="absolute z-50 flex items-center gap-0.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-1"
               style={{
                  top: bubbleMenuPos.top,
                  left: bubbleMenuPos.left,
                  transform: 'translateX(-50%)',
               }}
               onMouseDown={(e) => e.preventDefault()}
            >
               <ToolbarButton
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  isActive={editor.isActive('bold')}
                  title="Bold"
               >
                  <Bold size={iconSize} />
               </ToolbarButton>
               <ToolbarButton
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  isActive={editor.isActive('italic')}
                  title="Italic"
               >
                  <Italic size={iconSize} />
               </ToolbarButton>
               <ToolbarButton
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  isActive={editor.isActive('underline')}
                  title="Underline"
               >
                  <UnderlineIcon size={iconSize} />
               </ToolbarButton>
               <ToolbarButton
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  isActive={editor.isActive('strike')}
                  title="Strikethrough"
               >
                  <Strikethrough size={iconSize} />
               </ToolbarButton>
               <ToolbarButton
                  onClick={() => editor.chain().focus().toggleCode().run()}
                  isActive={editor.isActive('code')}
                  title="Inline Code"
               >
                  <Code size={iconSize} />
               </ToolbarButton>
            </div>
         )}

         <div className="flex-1 overflow-y-auto">
            <EditorContent
               editor={editor}
               className="prose prose-lg max-w-none w-full focus:outline-none min-h-full p-4 dark:prose-invert prose-gray dark:text-gray-100"
               style={{
                  color: 'inherit',
               }}
            />
         </div>

         {/* Character/Word Count Footer */}
         {editor && (
            <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-1.5 flex justify-end gap-4 text-xs text-gray-400 dark:text-gray-500 shrink-0">
               <span>{editor.storage.characterCount.words()} words</span>
               <span>{editor.storage.characterCount.characters()} characters</span>
            </div>
         )}
      </div>
   );
};

export default TipTapEditor;
